import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Building2, Loader2, Mail, Pencil, Phone, Plus, Search, Trash2, User } from 'lucide-react';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { ConfirmationModal, RefreshButton } from '@/components/core';
import { useDebounce } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';
import { useCompanyLookup } from '@/modules/org/companies/hooks/use-companies';
import { useGetOrganizationByOrgId } from '@/graphql/queries/organizations';
import { useGetContacts, useDeleteContacts } from '@/graphql';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import { ContactFormDialog } from '../components/contact-form-dialog';
import type { ContactScope } from '../types/contacts.types';
import type { ContactItem } from '@/graphql/queries/contacts/use-get-contacts';
import { ContactListProps } from './types';

/** `companyName` is the resolved company, which may differ from the stored snapshot. */
const matches = (contact: ContactItem, term: string, companyName: string) => {
  if (!term) return true;
  const q = term.toLowerCase();
  return (
    contact.Name.toLowerCase().includes(q) ||
    (contact.Email ?? '').toLowerCase().includes(q) ||
    companyName.toLowerCase().includes(q) ||
    (contact.Role ?? '').toLowerCase().includes(q)
  );
};

export default function OrgContactsPage() {
  const { t } = useTranslation();
  const { orgId: slug } = useParams<{ orgId: string }>();
  const { data: org } = useGetOrganizationByOrgId(slug);
  const orgName = org?.Name ?? '';

  const orgId = useCurrentOrgId();
  const { data, isLoading } = useGetContacts(
    {
      where: { OrganizationId: { eq: orgId } },
      paging: { pageNo: 1, pageSize: 200 },
    },
    { enabled: !!orgId }
  );
  const { deleteContacts } = useDeleteContacts();
  const [tab, setTab] = useState<ContactScope>('user');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ContactItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContactItem | null>(null);

  const all = useMemo(() => data?.getContactss?.items ?? [], [data]);
  const counts = useMemo(
    () => ({
      user: all.filter((c) => c.Scope !== 'organization').length,
      organization: all.filter((c) => c.Scope === 'organization').length,
    }),
    [all]
  );

  // Only pay for the companies fetch once some contact actually carries a link.
  const hasLinkedCompanies = useMemo(() => all.some((c) => !!c.CompanyId), [all]);
  const { byId: companyById } = useCompanyLookup(hasLinkedCompanies);

  /** Live company name, falling back to the stored snapshot when the id can't be resolved. */
  const companyNameOf = useCallback(
    (contact: ContactItem) =>
      (contact.CompanyId ? companyById.get(contact.CompanyId)?.name : '') || contact.Company || '',
    [companyById]
  );

  const filtered = useMemo(
    () =>
      all.filter(
        (c) =>
          (tab === 'organization' ? c.Scope === 'organization' : c.Scope !== 'organization') &&
          matches(c, debouncedSearch.trim(), companyNameOf(c))
      ),
    [all, tab, debouncedSearch, companyNameOf]
  );

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (contact: ContactItem) => {
    setEditing(contact);
    setDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deleteTarget) deleteContacts({ ItemId: deleteTarget.ItemId, isHardDelete: true });
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-6 font-nunito">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <div className="flex justify-between items-center">
          <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--fg))] font-heading">
            {t('ORGCONTACTS_TITLE')}
          </h1>

          <div className="flex shrink-0 items-center gap-3">
            <RefreshButton
              className="hidden md:flex"
              queryKey={['contacts']}
              label={t('ORGTEAM_HEADER_REFRESH')}
            />
            <Button variant="primary" size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('ORGCONTACTS_ADD')}
            </Button>
          </div>
        </div>
        <p className="mt-1 text-globe-grey">{t('ORGCONTACTS_SUBTITLE')}</p>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as ContactScope)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="user" className="gap-1.5 flex-1 md:flex-none">
              <User className="h-4 w-4" />
              {t('ORGCONTACTS_TAB_MINE')}
              <Badge variant="secondary">{counts.user}</Badge>
            </TabsTrigger>
            <TabsTrigger value="organization" className="gap-1.5 flex-1 md:flex-none">
              <Building2 className="h-4 w-4" />
              {orgName || t('ORGCONTACTS_TAB_ORG')}
              <Badge variant="secondary">{counts.organization}</Badge>
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-globe-grey" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('ORGCONTACTS_SEARCH_PLACEHOLDER')}
                aria-label={t('ORGCONTACTS_SEARCH_PLACEHOLDER')}
                className="h-9 pl-9"
              />
            </div>
            <RefreshButton
              className="md:hidden"
              queryKey={['contacts']}
              label={t('ORGTEAM_HEADER_REFRESH')}
            />
          </div>
        </div>

        <TabsContent value={tab} className="mt-4">
          <ContactList
            contacts={filtered}
            isLoading={isLoading}
            scope={tab}
            orgName={orgName}
            companyNameOf={companyNameOf}
            onAdd={openCreate}
            onEdit={openEdit}
            onDelete={setDeleteTarget}
          />
        </TabsContent>
      </Tabs>

      <ContactFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        contact={editing}
        defaultShareWithOrg={!editing && tab === 'organization'}
        orgName={orgName}
      />

      <ConfirmationModal
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('ORGCONTACTS_DELETE_CONFIRM_TITLE')}
        description={t('ORGCONTACTS_DELETE_CONFIRM_DESCRIPTION', {
          name: deleteTarget?.Name ?? '',
        })}
        onConfirm={confirmDelete}
        confirmText="DELETE"
        cancelText="CANCEL"
      />
    </div>
  );
}



function ContactList({
  contacts,
  isLoading,
  scope,
  orgName,
  companyNameOf,
  onAdd,
  onEdit,
  onDelete,
}: ContactListProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-globe-grey">
        <Loader2 className="h-5 w-5 animate-spin" />
        {t('ORGCONTACTS_LOADING')}
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <User className="h-12 w-12 text-globe-grey-500 mb-4" />
          <p className="text-globe-grey mb-4">
            {scope === 'organization'
              ? t('ORGCONTACTS_EMPTY_ORG', { org: orgName || t('ORGCONTACTS_YOUR_ORG') })
              : t('ORGCONTACTS_EMPTY_MINE')}
          </p>
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus className="h-4 w-4" />
            {t('ORGCONTACTS_ADD')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <ul className="overflow-hidden rounded-xl border border-border bg-background divide-y divide-border">
      {contacts.map((contact) => {
        const secondary = [contact.Role, companyNameOf(contact)].filter(Boolean).join(' · ');
        const Icon = contact.Scope === 'organization' ? Building2 : User;
        return (
          <li key={contact.ItemId} className="flex items-center gap-4 px-4 py-3">
            <div className="h-10 w-10 shrink-0 rounded-full bg-selise-blue/10 flex items-center justify-center">
              <Icon className="h-5 w-5 text-selise-blue" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold text-[hsl(var(--fg))]">
                {contact.Name}
              </p>
              {secondary && <p className="truncate text-sm text-globe-grey">{secondary}</p>}
              <div className="mt-0.5 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-sm text-globe-grey">
                {contact.Email && (
                  <span className="inline-flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" />
                    {contact.Email}
                  </span>
                )}
                {contact.Phone && (
                  <span className="inline-flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5" />
                    {contact.Phone}
                  </span>
                )}
              </div>
            </div>

            <div className={cn('flex shrink-0 items-center gap-1')}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-globe-grey"
                onClick={() => onEdit(contact)}
                aria-label={t('EDIT')}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-crimson hover:text-crimson"
                onClick={() => onDelete(contact)}
                aria-label={t('DELETE')}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
